import classnames from "classnames";
import PropTypes from "prop-types";

import { useEnableNewNavigationButtons } from "components/hooks/feature-flags";
import { useTranslation } from "components/i18n/source";
import {
  localizeHref,
  useAppContext,
  userRoutes,
} from "components/store/source";
import { Button, Dropdown, Hamburger, Link, Text } from "components/ui/source";
import CurrencySelector from "components/ui/source/components/Selector/CurrencySelector";
import LanguageSelector from "components/ui/source/components/Selector/LanguageSelector";
import { desktopHeaderLinks } from "components/ui/source/util/constants";

import { ACCOUNT_TYPE } from "types/sonder/source";

import FindYourReservation from "./FindYourReservation";
import { useUserActions } from "./useUserActions";

import styles from "./ActionsContainer.module.scss";

function ActionsContainer({
  account,
  screen,
  orientation,
  confirmationPopupClosed,
  openConfirmationPopup,
  closeConfirmationPopup,
  isAirbnb,
  showLoginButton,
}) {
  const { t } = useTranslation();
  const isDesktop = screen === "desktop";
  const isMobile = screen !== "desktop";
  const {
    openLoginPopup,
    openSignUpPopup,
    openLookUpPopup,
    openLoginOptionsPopup,
  } = useAppContext();
  const enableNewNavigationButtons = useEnableNewNavigationButtons();

  const SelectorActions = () => (
    <>
      <CurrencySelector className={styles.selector} isMobile={isMobile} />
      <LanguageSelector className={styles.selector} isMobile={isMobile} />
    </>
  );

  const DefaultActions = () => {
    if (isAirbnb) {
      return <SelectorActions />;
    }
    return (
      <>
        <SelectorActions />
        {!enableNewNavigationButtons && (
          <FindYourReservation
            confirmationPopupClosed={confirmationPopupClosed}
            openConfirmationPopup={openConfirmationPopup}
            closeConfirmationPopup={closeConfirmationPopup}
            openSignUpPopup={() => openSignUpPopup({ source: "Check In" })}
          />
        )}
        {enableNewNavigationButtons && (
          <Button
            type="navbar_signup"
            onClick={() =>
              openLookUpPopup({ source: "Navbar", buttonSource: "My Stays" })
            }
            upperCase={false}
            textProps={{
              translateKey: "navbar.userActions.find_my_stays",
              types: ["light", "inline", "body1"],
            }}
          />
        )}
        <Button
          type="navbar_signup"
          onClick={() =>
            enableNewNavigationButtons
              ? openLoginOptionsPopup({
                  source: "Navbar",
                  buttonSource: "Log in",
                })
              : openLoginPopup({ source: "Navbar" })
          }
          upperCase={false}
          textProps={{
            translateKey: "navbar.logIn",
            types: ["light", "inline", "body1"],
          }}
        />
        {!enableNewNavigationButtons && (
          <Button
            type="navbar_signup"
            onClick={() => openSignUpPopup({ source: "Navbar" })}
            upperCase={false}
            textProps={{
              translateKey: "navbar.signUp",
              types: ["medium", "inline", "body1"],
            }}
          />
        )}
      </>
    );
  };

  const UserActions = () => {
    const firstName = account.firstName;
    const lastName = account.lastName && `${account.lastName.slice(0, 1)}.`;
    const accountName = `${firstName} ${lastName}`;
    const userActions = useUserActions();

    // To support new locale correctly, we need to specify redirect path
    // TODO: We can remove this when cleaning up old locales from flatbook
    const customUserRoutes = Object.assign({}, userRoutes);
    // Add sign_out to redirect path to trigger analytics.reset() on logout
    customUserRoutes.log_out += `?redirect_path=${
      (global?.location?.origin ?? "") + localizeHref("/")
    }%3Fsign_out`;

    if (isAirbnb) {
      return <SelectorActions />;
    }

    return (
      <>
        <SelectorActions />
        {enableNewNavigationButtons && (
          <Link
            textTypes={["body1", "light"]}
            className={styles.user_action_text}
            translateKey="navbar.userActions.my_stays"
            href={customUserRoutes["my_stays"]}
            useLocalizedHref={true}
            navButton
          />
        )}
        {showLoginButton ? (
          <Button
            type="navbar_signup"
            onClick={() =>
              enableNewNavigationButtons
                ? openLoginOptionsPopup({ source: "Navbar", forceLogout: true })
                : openLoginPopup({ source: "Navbar" })
            }
            upperCase={false}
            textProps={{
              translateKey: "navbar.logIn",
              types: ["light", "inline", "body1"],
            }}
          />
        ) : (
          <Dropdown
            alignRight
            buttonType="navbar_signup"
            label={
              <div
                className={styles.dropdown_label}
                aria-label="user_actions_label"
              >
                <Text
                  types={["body1", "bold", "medium", "capitalized", "inline"]}
                >
                  <span aria-hidden="true">{accountName}</span>
                  <span className="sr-only">
                    {t("navbar.userDropdown", { accountName })}
                  </span>
                </Text>
              </div>
            }
            outlineOnFocus={false}
          >
            <ul className={classnames(styles.user_actions_row)}>
              {!enableNewNavigationButtons && (
                <FindYourReservation
                  confirmationPopupClosed={confirmationPopupClosed}
                  openConfirmationPopup={openConfirmationPopup}
                  closeConfirmationPopup={closeConfirmationPopup}
                  openSignUpPopup={() =>
                    openSignUpPopup({ source: "Check In" })
                  }
                  inUserActions
                />
              )}
              {userActions.map(action => (
                <li className={styles.user_action} key={action}>
                  <Link
                    textTypes={["body1", "light"]}
                    className={styles.user_action_text}
                    translateKey={`navbar.userActions.${action}`}
                    href={customUserRoutes[action]}
                    // Do not localize logout link
                    useLocalizedHref={action !== "log_out"}
                  />
                </li>
              ))}
            </ul>
          </Dropdown>
        )}
      </>
    );
  };

  const MobileUserActions = () => {
    const firstName = account.firstName;
    const lastName = account.lastName && `${account.lastName.slice(0, 1)}.`;
    const userActions = useUserActions();

    if (isAirbnb) {
      return <SelectorActions />;
    }

    return (
      <>
        {!showLoginButton && (
          <Text types={["body1", "inline"]}>
            {firstName} {lastName}
          </Text>
        )}
        <ul className={styles.user_actions_column}>
          {enableNewNavigationButtons && (
            <li className={styles.user_action}>
              <Link
                textTypes={["body1", "light"]}
                className={styles.user_action_text}
                translateKey="navbar.userActions.my_stays"
                href={userRoutes["my_stays"]}
              />
            </li>
          )}
          {showLoginButton && (
            <li className={styles.user_action}>
              <Link
                onClick={() =>
                  enableNewNavigationButtons
                    ? openLoginOptionsPopup({
                        source: "Navbar",
                        forceLogout: true,
                      })
                    : openLoginPopup({ source: "Navbar" })
                }
                className={styles.user_action_text}
                translateKey={"navbar.logIn"}
                textTypes={["body1", "light"]}
              />
            </li>
          )}
          {userActions.map(action => (
            <li className={styles.user_action} key={action}>
              <Link
                textTypes={["body1", "light"]}
                className={styles.user_action_text}
                translateKey={`navbar.userActions.${action}`}
                href={userRoutes[action]}
              />
            </li>
          ))}
          <li>
            <LanguageSelector isMobile={true} />
          </li>
          <li>
            <CurrencySelector isMobile={true} />
          </li>
        </ul>
      </>
    );
  };

  const renderUserMenu = () =>
    isMobile ? <MobileUserActions /> : <UserActions />;

  const renderMenu = () => {
    return (
      <>
        {account.anonymous ? <DefaultActions /> : renderUserMenu()}

        {isDesktop && !isAirbnb && (
          <Dropdown
            buttonType="navbar_signup"
            label={<Hamburger />}
            alignRight
            noIcon
            outlineOnFocus={false}
          >
            <ul className={classnames(styles.user_actions_row)}>
              {Object.keys(desktopHeaderLinks).map(link => (
                <li className={styles.user_action} key={link}>
                  <Link
                    textTypes={["body1", "light"]}
                    className={styles.user_action_text}
                    translateKey={`navbar.navLinks.${link}`}
                    href={desktopHeaderLinks[link]}
                    // Do not localize blog link
                    useLocalizedHref={link !== "blog"}
                    // Redirect to new tab only on blog link
                    newTab={link === "blog"}
                  />
                </li>
              ))}
            </ul>
          </Dropdown>
        )}
      </>
    );
  };

  return (
    <div className={styles[`actions-container-${orientation}`]}>
      {renderMenu()}
    </div>
  );
}

ActionsContainer.propTypes = {
  account: ACCOUNT_TYPE.isRequired,
  screen: PropTypes.oneOf(["desktop", "mobile"]),
  orientation: PropTypes.oneOf(["row", "column"]),
  confirmationPopupClosed: PropTypes.bool,
  openConfirmationPopup: PropTypes.func,
  closeConfirmationPopup: PropTypes.func,
  isAirbnb: PropTypes.bool,
};

ActionsContainer.defaultProps = {
  screen: "desktop",
  orientation: "row",
  confirmationPopupClosed: null,
  openConfirmationPopup: null,
  closeConfirmationPopup: null,
  isAirbnb: false,
};

export default ActionsContainer;
